import { format, addWeeks, addDays, isBefore, isAfter, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';

export interface LeagueWeek {
  number: number;
  date: string;
  dateEnd?: string;
  fullDate: Date;
  status: 'past' | 'current' | 'future';
  time: string;
}

/**
 * Generates league weeks dynamically based on database values
 * @param startDate - The start date of the league (YYYY-MM-DD format)
 * @param totalWeeks - Total number of weeks/matches in the league
 * @param startTime - Start time in HH:MM:SS format (optional)
 * @param endTime - End time in HH:MM:SS format (optional)
 * @returns Array of LeagueWeek objects
 */
export function generateLeagueWeeks(
  startDate: string,
  totalWeeks: number,
  startTime?: string,
  endTime?: string,
  sport?: string
): LeagueWeek[] {
  const weeks: LeagueWeek[] = [];
  
  // Validate startDate
  if (!startDate || typeof startDate !== 'string') {
    console.warn('generateLeagueWeeks: Invalid startDate provided:', startDate);
    return weeks;
  }
  
  // Parse date as local to avoid UTC offset issues
  // YYYY-MM-DD parsed with new Date() is interpreted as UTC midnight,
  // which causes a 1-day shift in negative UTC offset timezones (e.g., Mexico)
  let start: Date;
  
  // Check if it's in YYYY-MM-DD format
  const dateMatch = startDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(dateMatch[3], 10);
    start = startOfDay(new Date(year, month, day));
  } else {
    // Fallback for other date formats
    start = startOfDay(new Date(startDate));
  }
  
  // Validate the parsed date
  if (isNaN(start.getTime())) {
    console.warn('generateLeagueWeeks: Could not parse startDate:', startDate);
    return weeks;
  }
  
  const today = startOfDay(new Date());
  
  // Format time range in AM/PM
  const timeRange = startTime && endTime 
    ? `${formatTo12h(startTime)} - ${formatTo12h(endTime)}`
    : '';
  
  for (let i = 0; i < totalWeeks; i++) {
    const weekDate = addWeeks(start, i);
    const weekDateNormalized = startOfDay(weekDate);
    
    // Determine week status based on current date
    let status: 'past' | 'current' | 'future';
    
    if (isBefore(weekDateNormalized, today)) {
      status = 'past';
    } else if (weekDateNormalized.getTime() === today.getTime()) {
      status = 'current';
    } else if (isAfter(weekDateNormalized, today)) {
      status = 'future';
    } else {
      status = 'future';
    }
    
    const isTennis = sport?.toLowerCase() === 'tennis' || sport?.toLowerCase() === 'tenis';
    
    weeks.push({
      number: i + 1,
      date: format(weekDate, 'd MMM', { locale: es }),
      dateEnd: isTennis ? format(addDays(weekDate, 6), 'd MMM', { locale: es }) : undefined,
      fullDate: weekDate,
      status,
      time: timeRange
    });
  }
  
  // If no current week found, mark the first future week as current
  const hasCurrentWeek = weeks.some(w => w.status === 'current');
  if (!hasCurrentWeek) {
    const firstFutureIndex = weeks.findIndex(w => w.status === 'future');
    if (firstFutureIndex !== -1) {
      weeks[firstFutureIndex].status = 'current';
    }
  }
  
  return weeks;
}

/**
 * Calculates remaining weeks in a league
 * @param weeks - Array of LeagueWeek objects
 * @returns Number of future weeks remaining
 */
export function calculateWeeksRemaining(weeks: LeagueWeek[]): number {
  return weeks.filter(w => w.status === 'future').length;
}

/**
 * Converts a time string (HH:MM or HH:MM:SS) to 12-hour AM/PM format
 */
export function formatTo12h(time: string): string {
  const [hStr, mStr] = time.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr?.slice(0, 2) || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ampm}`;
}

/**
 * Formats time range from start and end time strings in AM/PM format
 */
export function formatTimeRange(startTime?: string, endTime?: string): string {
  if (startTime && endTime) {
    return `${formatTo12h(startTime)} - ${formatTo12h(endTime)}`;
  }
  return '';
}

/**
 * Calculates the end date of a week (start + 6 days) formatted as "d MMM" in Spanish
 * Only for tennis leagues
 */
export function calculateWeekEndDate(startDate: Date): string {
  const endDate = addDays(startDate, 6);
  return format(endDate, 'd MMM', { locale: es });
}

/**
 * Interleaves rest weeks into an encounter-only weeks array.
 * Rest weeks are inserted at their chronological positions, and encounter weeks
 * keep their original encounter number but get a new chronological `number`.
 * This keeps the calendar in sync with court booking's chronoWeekMapping.
 */
export function interleaveRestWeeks(
  encounterWeeks: LeagueWeek[],
  restWeeks: number[],
  sport?: string
): (LeagueWeek & { isRest?: boolean; encounterNumber?: number })[] {
  if (!restWeeks || restWeeks.length === 0) {
    return encounterWeeks;
  }

  const baseDate = encounterWeeks.length > 0 ? encounterWeeks[0].fullDate : null;
  if (!baseDate) return encounterWeeks;

  const totalChronoWeeks = encounterWeeks.length + restWeeks.length;
  const result: (LeagueWeek & { isRest?: boolean; encounterNumber?: number })[] = [];
  let encounterIndex = 0;
  const isTennis = sport?.toLowerCase() === 'tennis' || sport?.toLowerCase() === 'tenis';

  for (let chronoWeek = 1; chronoWeek <= totalChronoWeeks; chronoWeek++) {
    if (restWeeks.includes(chronoWeek)) {
      // Rest week — calculate date from base
      const weekStart = addDays(baseDate, (chronoWeek - 1) * 7);
      const today = startOfDay(new Date());
      const weekStartNorm = startOfDay(weekStart);
      let status: 'past' | 'current' | 'future' = 'future';
      if (isBefore(weekStartNorm, today)) status = 'past';
      else if (weekStartNorm.getTime() === today.getTime()) status = 'current';

      result.push({
        number: chronoWeek,
        date: format(weekStart, 'd MMM', { locale: es }),
        dateEnd: isTennis ? format(addDays(weekStart, 6), 'd MMM', { locale: es }) : undefined,
        fullDate: weekStart,
        status,
        time: '',
        isRest: true,
      });
    } else {
      // Encounter week — recalculate date from chronological position
      if (encounterIndex < encounterWeeks.length) {
        const ew = encounterWeeks[encounterIndex];
        const weekStart = addDays(baseDate, (chronoWeek - 1) * 7);
        const weekStartNorm = startOfDay(weekStart);
        const today = startOfDay(new Date());
        let status: 'past' | 'current' | 'future' = 'future';
        if (isBefore(weekStartNorm, today)) status = 'past';
        else if (weekStartNorm.getTime() === today.getTime()) status = 'current';

        result.push({
          ...ew,
          number: chronoWeek,
          date: format(weekStart, 'd MMM', { locale: es }),
          dateEnd: isTennis ? format(addDays(weekStart, 6), 'd MMM', { locale: es }) : undefined,
          fullDate: weekStart,
          status,
          encounterNumber: encounterIndex + 1,
          isRest: false,
        });
        encounterIndex++;
      }
    }
  }

  return result;
}
